import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import './AuthPage.css';
import { AuthMethods } from './AuthMethods';
import { ResendVerification } from './ResendVerification';
import leaderid from '../../assets/icons/leaderid.svg';
import tracksSvgUrl from '../../assets/icons/треки.svg';
import eyeclosed from '../../assets/icons/eyeclosed.svg';
import eyeopen from '../../assets/icons/eye.svg';
import {
  getLeaderIdAuthorizationUrl,
  login as loginRequest
} from '../../api/auth';
import { useAuth } from '../../context/AuthContext';
import {
  mapApiError,
  validateLoginOrEmail,
  validateLoginPassword
} from '../../utils/validators';

export function Login() {
  const [email, setEmail] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLeaderIdSubmitting, setIsLeaderIdSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSession } = useAuth();

  useEffect(() => {
    const message = location?.state?.infoMessage;
    if (!message) return;
    setInfoMessage(String(message));
    if (location.state.email) setEmail(String(location.state.email));
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, navigate]);

  const stageRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    const SVG_NS = 'http://www.w3.org/2000/svg';
    const TRACKS_FALLBACK = [
      { y: 0, height: 506.25, fill: '#070731' },
      { y: 506, height: 506.25, fill: '#171717' },
      { y: 1013, height: 506.25, fill: '#4C56E6' },
      { y: 1519, height: 506.25, fill: '#F5F5F5' }
    ];

    const stageEl = stageRef.current;
    const layerEl = layerRef.current;
    const animations = [];

    function setStageBackground(tracks, totalHeight) {
      if (!stageEl) return;
      const c1 = tracks[0];
      const c2 = tracks[1];
      const c3 = tracks[2];
      const c4 = tracks[3];
      const p1 = ((c1.y + c1.height) / totalHeight) * 100;
      const p2 = ((c2.y + c2.height) / totalHeight) * 100;
      const p3 = ((c3.y + c3.height) / totalHeight) * 100;

      stageEl.style.background = `linear-gradient(
        to bottom,
        ${c1.fill} 0%,
        ${c1.fill} ${p1}%,
        ${c2.fill} ${p1}%,
        ${c2.fill} ${p2}%,
        ${c3.fill} ${p2}%,
        ${c3.fill} ${p3}%,
        ${c4.fill} ${p3}%,
        ${c4.fill} 100%
      )`;
    }

    function extractTracks(svg) {
      const rects = [...svg.querySelectorAll('rect')]
        .map((r) => ({
          el: r,
          x: Number(r.getAttribute('x') || 0),
          y: Number(r.getAttribute('y') || 0),
          w: Number(r.getAttribute('width') || 0),
          h: Number(r.getAttribute('height') || 0),
          fill: r.getAttribute('fill') || 'transparent'
        }))
        .filter((r) => r.w >= 3500 && r.h >= 350 && r.h < 1000)
        .sort((a, b) => a.y - b.y)
        .slice(0, 4)
        .map((r) => ({ y: r.y, height: r.h, fill: r.fill }));

      return rects.length === 4 ? rects : TRACKS_FALLBACK;
    }

    function isBackgroundRect(el) {
      const w = Number(el.getAttribute('width') || 0);
      const h = Number(el.getAttribute('height') || 0);
      return w >= 3500 && h >= 350;
    }

    function assignTrackByY(yCenter, tracks) {
      for (let i = 0; i < tracks.length; i += 1) {
        const t = tracks[i];
        if (yCenter >= t.y && yCenter < t.y + t.height) return i;
      }
      return 0;
    }

    function animateTrackGroup(group, trackIndex, totalWidth) {
      const toLeft = trackIndex % 2 === 0;
      const dir = toLeft ? -1 : 1;
      const duration = 22000;

      const content = document.createElementNS(SVG_NS, 'g');
      while (group.firstChild) content.appendChild(group.firstChild);

      const copy = content.cloneNode(true);
      const offset = toLeft ? totalWidth : -totalWidth;
      copy.setAttribute('transform', `translate(${offset} 0)`);

      group.appendChild(content);
      group.appendChild(copy);

      const anim = group.animate(
        [
          { transform: 'translateX(0px)' },
          { transform: `translateX(${dir * totalWidth}px)` }
        ],
        {
          duration,
          iterations: Infinity,
          easing: 'linear'
        }
      );

      return anim;
    }

    async function init() {
      if (!stageEl || !layerEl) return;
      setStageBackground(TRACKS_FALLBACK, 2025);

      try {
        const res = await fetch(tracksSvgUrl);
        if (!res.ok) return;
        const text = await res.text();

        const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
        const srcSvg = doc.documentElement;
        if (!srcSvg || srcSvg.tagName.toLowerCase() !== 'svg') return;

        const vb = (srcSvg.getAttribute('viewBox') || '0 0 3600 2025').split(/\s+/).map(Number);
        const totalW = vb[2] || 3600;
        const totalH = vb[3] || 2025;

        layerEl.setAttribute('viewBox', `0 0 ${totalW} ${totalH}`);

        const tracks = extractTracks(srcSvg);
        setStageBackground(tracks, totalH);

        const groups = tracks.map((t, idx) => {
          const g = document.createElementNS(SVG_NS, 'g');
          g.setAttribute('data-track', String(idx + 1));
          layerEl.appendChild(g);
          return g;
        });

        const measureHost = document.createElement('div');
        measureHost.style.position = 'fixed';
        measureHost.style.left = '-99999px';
        measureHost.style.top = '-99999px';
        measureHost.style.width = '1px';
        measureHost.style.height = '1px';
        measureHost.style.overflow = 'hidden';
        measureHost.style.pointerEvents = 'none';
        measureHost.style.opacity = '0';

        const measureSvg = srcSvg.cloneNode(true);
        measureHost.appendChild(measureSvg);
        document.body.appendChild(measureHost);

        const sourceItems = [...measureSvg.querySelectorAll('path, rect')];

        sourceItems.forEach((el) => {
          if (el.tagName.toLowerCase() === 'rect' && isBackgroundRect(el)) return;

          let box;
          try {
            box = el.getBBox();
          } catch {
            return;
          }

          if (!box || !Number.isFinite(box.y)) return;
          const yCenter = box.y + box.height / 2;
          const idx = assignTrackByY(yCenter, tracks);

          if (
            idx === 0 &&
            box.y < 150 &&
            box.x > 1600 &&
            box.x < 2050 &&
            box.height < 40
          ) {
            return;
          }

          groups[idx].appendChild(el.cloneNode(true));
        });

        measureHost.remove();

        groups.forEach((g, i) => {
          const a = animateTrackGroup(g, i, totalW);
          if (a) animations.push(a);
        });
      } catch {
        // ignore animation asset parsing errors
      }
    }

    init();

    return () => {
      animations.forEach((a) => {
        try {
          a.cancel();
        } catch {}
      });
      if (layerEl) {
        while (layerEl.firstChild) layerEl.removeChild(layerEl.firstChild);
      }
    };
    // eslint-disable-next-line
  }, [tracksSvgUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextFieldErrors = {
      email: validateLoginOrEmail(email),
      password: validateLoginPassword(password)
    };
    setFieldErrors(nextFieldErrors);
    if (nextFieldErrors.email || nextFieldErrors.password) {
      return;
    }

    try {
      setError('');
      setInfoMessage('');
      setIsSubmitting(true);
      const normalizedCredential = email.trim();
      const loginOrEmail = normalizedCredential.includes('@')
        ? normalizedCredential
        : normalizedCredential.replace(/\s+/g, '_');

      await loginRequest(loginOrEmail, password);
      await refreshSession();
      navigate('/my-season');
    } catch (err) {
      setError(mapApiError('login', err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeaderIdAuth = async () => {
    try {
      setError('');
      setIsLeaderIdSubmitting(true);
      const payload = await getLeaderIdAuthorizationUrl();
      if (!payload?.authorization_url) {
        throw new Error('Не удалось получить ссылку авторизации Leader-ID.');
      }
      window.location.assign(payload.authorization_url);
    } catch (err) {
      setError(err.message || 'Не удалось начать авторизацию через Leader-ID.');
      setIsLeaderIdSubmitting(false);
    }
  };


  return (
    <div className="auth-container">
      <div className="auth-image">
        <main
          className="stage"
          ref={stageRef}
          aria-label="Animated tracks banner"
          style={{ width: '100%', height: '100%', minHeight: '200px' }}
        >
          <svg
            id="objectsLayer"
            ref={layerRef}
            className="objects-layer"
            viewBox="0 0 3600 2025"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{ width: '100%', height: '100%' }}
          ></svg>
        </main>
      </div>

      <div className="auth-form-container">
        <div className="auth-card">
          <h1 className="auth-title auth-title-primary">Вход в ЮФУ.Про</h1>
          <AuthMethods forceAlternative={Boolean(infoMessage) || new URLSearchParams(location.search).get('method') === 'password'}>
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="login-email">
                Введите логин или почту
              </label>
              <input
                className="form-input"
                id="login-email"
                type="text"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setInfoMessage('');
                  setFieldErrors((prev) => ({ ...prev, email: '' }));
                }}
                onBlur={() =>
                  setFieldErrors((prev) => ({ ...prev, email: validateLoginOrEmail(email) }))
                }
                placeholder="Логин или email"
                required
              />
              {fieldErrors.email ? <p className="form-error">{fieldErrors.email}</p> : null}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">
                Введите пароль
              </label>
              <div className="password-input-wrapper">
                <input
                  className="form-input"
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setInfoMessage('');
                    setFieldErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  onBlur={() =>
                    setFieldErrors((prev) => ({
                      ...prev,
                      password: validateLoginPassword(password)
                    }))
                  }
                  placeholder="Введите пароль"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <img src={eyeopen} alt="" />
                  ) : (
                    <img src={eyeclosed} alt="" />
                  )}
                </button>
              </div>
              {fieldErrors.password ? <p className="form-error">{fieldErrors.password}</p> : null}
            </div>

            <button className="auth-button" disabled={isSubmitting}>
              {isSubmitting ? 'Входим...' : 'Войти'}
            </button>
          </form>
          {infoMessage ? <p className="auth-link auth-link-success">{infoMessage}</p> : null}
          {error && <p className="auth-link">{error}</p>}
          <p className="auth-link">
            <Link to="/forgot-password" className="auth-link-register">Забыли пароль?</Link>
          </p>
          <p className="auth-link">
            <button
              type="button"
              className="auth-link-button"
              onClick={() => setShowResend(value => !value)}
              aria-expanded={showResend}
              aria-controls="login-resend"
            >
              Не пришло письмо подтверждения?
            </button>
          </p>
          {showResend && <div id="login-resend"><ResendVerification initialEmail={email.includes('@') ? email : ''} /></div>}
          <p className="auth-support-note">
            По всем техническим доработкам и предложениям пишите на sfedupro@yandex.com.
          </p>

          <p className="auth-link">
            Нет аккаунта? <Link to="/register" className="auth-link-register">Зарегистрироваться</Link>
          </p>

          <button
            type="button"
            className="auth-leaderid"
            onClick={handleLeaderIdAuth}
            disabled={isLeaderIdSubmitting}
          >
            <img src={leaderid} alt="" />
            {isLeaderIdSubmitting ? 'Переходим в Leader-ID...' : 'Leader-ID'}
          </button>
          </AuthMethods>
        </div>
      </div>
    </div>
  );
}
